import React from "react";
import { CheckCircle, TrendingUp, Users, Shield } from "lucide-react";

const toAlignClass = (a) => {
  const v = String(a || "").toLowerCase().trim();
  if (v === "center") return "text-center";
  if (v === "right") return "text-right";
  if (v === "left") return "text-left";
  return "";
};

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 180;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const FeaturesSection2 = ({ config, style }) => {
  const getIcon = (iconName) => {
    const icons = {
      "💼": <Users className="w-8 h-8" />,
      "💰": <TrendingUp className="w-8 h-8" />,
      "👥": <Shield className="w-8 h-8" />,
      default: <CheckCircle className="w-8 h-8" />,
    };
    return icons[iconName] || icons.default;
  };

  const bg = buildBg(style, "bg-gradient-to-b from-white to-blue-50");

  const align = (style?.textAlign || "").trim();
  const hasAlign = align.length > 0;

  const headerAlignClass = hasAlign ? toAlignClass(align) : "text-center";
  const cardAlignClass = hasAlign ? toAlignClass(align) : "";

  const rawLearn = config?.buttonText;
  const showLearn = rawLearn === undefined ? true : String(rawLearn).trim().length > 0;
  const learnLabel = rawLearn === undefined ? "Learn more →" : rawLearn;

  const primaryHasCustom = Boolean(
    (style?.primaryBtnBg || "").trim() || (style?.primaryBtnText || "").trim()
  );

  return (
    <section
      dir={config.direction || "ltr"}
      style={bg.style}
      className={`py-14 px-4 ${bg.className}`}
    >
      <div className="container mx-auto max-w-6xl">
        <div className={`${headerAlignClass} mb-16`}>
          <span
              style={{
                backgroundColor: style?.featuresBadgeBg || undefined,
                color: style?.featuresBadgeTextColor || undefined,
                borderColor: style?.featuresBadgeBorder || undefined,
              }}
              className={`inline-block px-4 py-2 rounded-full text-sm font-semibold mb-4 ${
                style?.featuresBadgeBg || style?.featuresBadgeTextColor || style?.featuresBadgeBorder
                  ? "border"
                  : "bg-blue-100 text-blue-600"
              }`}
            >
              {config.badgeText || "FEATURES"}
            </span>

          <h2
            style={{ color: style?.titleColor || undefined }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
          >
            {config.title}
          </h2>

          <p
            style={{ color: style?.subtitleColor || undefined }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {config.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {config.features?.map((feature, index) => (
           <div
              key={index}
              style={{
                backgroundColor: style?.featuresCardBg2 || undefined,
                borderColor: style?.featuresCardBorder2 || undefined,
              }}
              className={`rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border ${
                style?.featuresCardBorder2 ? "" : "border-gray-100"
              } ${style?.featuresCardBg2 ? "" : "bg-white"} ${cardAlignClass}`}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 text-white mb-8">
                {getIcon(feature.icon)}
              </div>

              <h3
                  style={{ color: style?.featuresCardTitleColor2 || undefined }}
                  className="text-2xl font-bold text-gray-900 mb-4"
                >
                {feature.title}
              </h3>

              <p
                  style={{ color: style?.featuresCardDescriptionColor2 || undefined }}
                  className="text-gray-600 mb-6"
                >
                  {feature.description}
                </p>

              <ul className="space-y-3">
                {feature.benefits?.map((benefit, i) => (
                  <li
                      key={i}
                      style={{ color: style?.featuresBenefitColor2 || undefined }}
                      className="flex items-center text-gray-700"
                    >
                    <CheckCircle className="w-5 h-5 text-green-500 me-3" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>

              {showLearn && (
                <button
                  style={
                    primaryHasCustom
                      ? { color: style?.primaryBtnText || undefined }
                      : undefined
                  }
                  className={`mt-8 font-semibold flex items-center ${
                    primaryHasCustom
                      ? "hover:opacity-80"
                      : "text-blue-600 hover:text-blue-800"
                  }`}
                >
                  {learnLabel}
                </button>
              )}
            </div>
          ))}
        </div>

        {config.bottomCta && (
          <div
            style={{
              backgroundImage:
                style?.featuresBottomCtaBgFrom && style?.featuresBottomCtaBgTo
                  ? `linear-gradient(90deg, ${style.featuresBottomCtaBgFrom}, ${style.featuresBottomCtaBgTo})`
                  : undefined,
            }}
            className={`mt-20 rounded-3xl p-8 ${
              style?.featuresBottomCtaBgFrom && style?.featuresBottomCtaBgTo
                ? ""
                : "bg-gradient-to-r from-blue-600 to-purple-600"
            } ${hasAlign ? toAlignClass(align) : "text-center"}`}
          >
            <h3
              style={{ color: style?.featuresBottomCtaTitleColor || undefined }}
              className="text-2xl font-bold text-white mb-4"
            >
              {config.bottomCta.title}
            </h3>
            <p
              style={{ color: style?.featuresBottomCtaSubtitleColor || undefined }}
              className="text-blue-100"
            >
              {config.bottomCta.subtitle}
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturesSection2;